import { isValidIban } from "common/Iban";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Spinner } from "reactstrap";
import { getOpenInvoicePaymentFile, getWorkerProjects } from "services/AccountApi";
import { useAccountTranslation } from "../translations";

interface Props {
    customer: Account;
    invoices: Invoice[];
    title: string;
}

export const InvoicePaymentFileButton: React.FC<Props> = ({ customer, invoices, title }) => {
    const { t } = useAccountTranslation();
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const [isProjectResponsible, setIsProjectResponsible] = useState(false);
    const openInvoices = useMemo(() => invoices.filter(invoice =>
        invoice.customer === customer.id && !invoice.quote && !invoice.draft && invoice.CalcPendingTotal() > 0.009
    ), [customer.id, invoices]);
    const hasValidIban = (customer.bank_accounts ?? []).some(bankAccount => isValidIban(bankAccount.iban));

    useEffect(() => {
        let active = true;
        setIsProjectResponsible(false);

        if (openInvoices.length === 0) return () => { active = false; };

        getWorkerProjects(customer.id)
            .then(projects => {
                if (active) {
                    setIsProjectResponsible(projects.some(project => project.responsible?.includes(customer.id)));
                }
            })
            .catch(() => {
                if (active) setIsProjectResponsible(false);
            });

        return () => { active = false; };
    }, [customer.id, openInvoices.length]);

    const download = async () => {
        setDownloading(true);
        setDownloadError(false);
        try {
            const paymentFile = await getOpenInvoicePaymentFile(customer.id);
            const url = URL.createObjectURL(new Blob([paymentFile.xml], { type: "application/xml;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = paymentFile.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            setDownloadError(true);
        } finally {
            setDownloading(false);
        }
    };

    const showAction = openInvoices.length > 0 && isProjectResponsible;

    return <>
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
            <p className="text-muted text-uppercase fw-semibold mb-0">{title}</p>
            {showAction && <Button color="primary" size="sm" disabled={!hasValidIban || downloading} onClick={download}>
                {downloading
                    ? <Spinner size="sm" className="me-2" />
                    : <i className="mdi mdi-bank-transfer-out me-1" aria-hidden="true" />}
                {t("createPaymentFile", { count: openInvoices.length })}
            </Button>}
        </div>
        {showAction && !hasValidIban && <Alert color="warning" fade={false} className="py-2">
            <i className="mdi mdi-bank-alert me-2" aria-hidden="true" />
            {t("paymentFileIbanMissing")} <Link to="/my-details">{t("myDetails")}</Link>
        </Alert>}
        {showAction && downloadError && <Alert color="danger" fade={false} className="py-2">
            <i className="mdi mdi-alert-circle-outline me-2" aria-hidden="true" />
            {t("paymentFileDownloadError")}
        </Alert>}
    </>;
};

export default InvoicePaymentFileButton;
